<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            :showHeader="showTableHeader"
            :pagination="paginateTable"
            ajax-path="wideband/locations-not-ready-for-service"
        />
        <app-modal
            :show.sync="showAddModal"
            width="lg:w-1/4"
        >
            <template slot="header">Add NBN Location ID</template>
            <template slot="body">
                <app-input
                    v-model="addForm.nbn_location_id"
                    placeholder="NBN Location ID"
                    :error="addForm.errors.get('nbn_location_id')"
                    @updated="addForm.errors.clear('nbn_location_id')"
                    focus
                    required
                />
                <app-textarea 
                    v-model="addForm.description"
                    placeholder="Description"
                    :error="addForm.errors.get('description')"
                    @updated="addForm.errors.clear('description')"
                    required
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddModal = false"
                        :disable="addFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="store"
                        :loading="addFormIsLoading"
                    >Add</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEditModal"
            width="lg:w-1/4"
        >
            <template slot="header">Edit NBN Location ID</template>
            <template slot="body">
                <app-input
                    v-model="editForm.nbn_location_id"
                    placeholder="NBN Location ID"
                    :error="editForm.errors.get('nbn_location_id')"
                    @updated="editForm.errors.clear('nbn_location_id')"
                    focus
                    required
                />
                <app-textarea 
                    v-model="editForm.description"
                    placeholder="Description"
                    :error="editForm.errors.get('description')"
                    @updated="editForm.errors.clear('description')"
                    required
                />
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="editFormIsLoading"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="editFormIsLoading"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import Form from '@/helpers/Form';
    import Http from '@/helpers/Http';

    export default {
        props: {
            showTableHeader: {
                type: Boolean,
                default: true,
            },
            paginateTable: {
                type: Boolean,
                default: true,
            }
        },
        data() {
            return {
                showAddModal: false,
                showEditModal: false,
                addFormIsLoading: false,
                editFormIsLoading: false,
                refreshTable: false,
                addForm: new Form({
                    nbn_location_id: null,
                    description: null,
                }),
                editForm: new Form({
                    nbn_location_id: null,
                    description: null,
                }),
                columns: [
                    {
                        title: 'NBN Location ID',
                        field: 'nbn_location_id',
                        sorter: 'string',
                        minWidth: 180,
                    },
                    {
                        title: 'Description',
                        field: 'description',
                        sorter: 'string',
                        minWidth: 300,
                    },
                    {
                        title: 'RFS Date',
                        field: 'rfs_date',
                        sorter: 'string',
                        minWidth: 150,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return _cell.value;
                            }

                            return '--';
                        }
                    },
                    {
                        title: 'Service Class',
                        minWidth: 200,
                        formatter({_cell}) {
                            if (_cell.row.data.service_class_number) {
                                return `(${_cell.row.data.service_class_number}) ${_cell.row.data.service_class_description}`;
                            }

                            return '--';
                        }
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        sorter: 'string',
                        minWidth: 200,
                        formatter({_cell}) {
                            if (_cell.value === 'Not Ready For Service') {
                                return `<span class='badge badge-red'>Not Ready For Service</span>`;
                            }

                            if (_cell.value === 'Ready For Service') {
                                return `<span class='badge badge-green'>${_cell.value}</span>`;
                            }

                            if (_cell.value === 'Plan Pending nbn') {
                                return `<span class='badge bg-orange-300 text-orange-800'>${_cell.value}</span>`;
                            }

                            return `<span class='badge bg-teal-300 text-teal-800'>${_cell.value}</span>`;
                        }
                    }
                ],
                actions: [
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showAddModal = true;
                            this.addForm.reset();
                            this.addForm.errors.clear();
                        }
                    },
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: (data) => {
                            this.selected = data[0];
                            this.addForm.errors.clear();
                            this.editForm.set(data[0]);
                            this.showEditModal = true;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: (data) => {
                            this.$swal.fire({
                                type: 'warning',
                                title: 'Are you sure?',
                                html: `You are about to delete "<strong>${data[0].nbn_location_id}</strong>".`,
                                showCancelButton: true,
                                confirmButtonColor: '#f56565',
                                confirmButtonText: 'Yes, delete it!',
                                showLoaderOnConfirm: true,
                                preConfirm: (response) => {
                                    return Http.delete(`wideband/locations-not-ready-for-service/${data[0].id}`);
                                },
                                allowOutsideClick: () => !this.$swal.isLoading()
                            })
                            .then((result) => {
                                if (result.value) {
                                    this.refreshTable = true;
                                    this.$swal.fire({
                                        type: 'success',
                                        title: 'Success!',
                                        text: 'Location was successfully deleted.'
                                    })
                                }
                            })
                        }
                    },
                ],
            }
        },
        methods: {
            store() {
                this.addFormIsLoading = true;
                this.addForm.errors.clear();
                Http.post('wideband/locations-not-ready-for-service', this.addForm.payload())
                    .then(() => {
                        this.addFormIsLoading = false;
                        this.showAddModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'NBN Location ID was added successfully.'
                        })
                    })
                    .catch(({ response }) => {
                        this.addFormIsLoading = false;
                        this.addForm.errors.record(response.data);
                    })
            },
            update() {
                this.editFormIsLoading = true;
                this.editForm.errors.clear();
                Http.patch(`wideband/locations-not-ready-for-service/${this.selected.id}`, this.editForm.payload())
                    .then(() => {
                        this.editFormIsLoading = false;
                        this.showEditModal = false;
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'NBN Location ID was successfully updated.'
                        })
                    })
                    .catch(({ response }) => {
                        this.editFormIsLoading = false;
                        this.editForm.errors.record(response.data);
                    })
            }
        }
    }
</script>